import { useCallback, useRef, useState } from 'react';
import type { SelectProps } from '@cloudscape-design/components/select';
import { api } from '../api/client';

export type OptionsState<T> =
  | { status: 'loading'; items: T[] }
  | { status: 'error'; items: T[]; message: string }
  | { status: 'finished'; items: T[] };

/** Throws the API's `reason` (or error code) when it answered 2xx with an `{ error, reason }` body. */
export function assertNoError(res: unknown) {
  const r = res as { error?: string; reason?: string } | null;
  if (r?.error) throw new Error(r.reason || r.error);
}

/**
 * Loads a list for a dropdown on demand (e.g. when a modal opens), so it is always fresh.
 * `parse` turns the response into items; the caller builds options from them.
 */
export function useOptions<T>(path: string, parse: (data: unknown) => T[]) {
  const [state, setState] = useState<OptionsState<T>>({ status: 'loading', items: [] });
  // Only the latest load may set state, so a slow earlier response (e.g. the previous app's) cannot overwrite it.
  const latestCall = useRef(0);

  const load = useCallback(() => {
    const call = ++latestCall.current;
    const set = (s: OptionsState<T>) => call === latestCall.current && setState(s);
    setState({ status: 'loading', items: [] });
    api('GET', path)
      .then(res => {
        assertNoError(res);
        set({ status: 'finished', items: parse(res) });
      })
      .catch(e => set({ status: 'error', items: [], message: (e as Error).message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parse is a module-level function
  }, [path]);

  return { state, load };
}

/** Select/Multiselect props for the loading, error and empty states of an options list. */
export function optionsStatusProps(state: OptionsState<unknown>, noun: string) {
  return {
    statusType: state.status,
    loadingText: `Loading ${noun}`,
    errorText: state.status === 'error' ? `Could not load ${noun}: ${state.message}` : undefined,
    empty: `This application has no ${noun}`,
    filteringType: 'auto' as const,
    filteringPlaceholder: `Search ${noun}`,
  } satisfies Partial<SelectProps>;
}
